import { useTranslation } from "react-i18next";
import {
  useAlphabet,
  useLoggedInAs,
  useCurrentAppRoute,
  useOperatorFilter,
} from "shared/hooks";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import {
  DirectoryKey,
  DirectoryViewSections,
} from "apps/directory/_directoryAppView.config";
import { IAppViewItem } from "shared/components";
import {
  OperatorManyUseCaseEnum,
  OperatorWithStatus,
  useOperatorManyQuery,
  useOperatorsByIdsLazyQuery,
} from "gql/generated";
import { ChangeEvent } from "react";
import { useToastContext } from "@ttc3k/trekker";
import { mapOperatorCSVData } from "shared/utils/mapOperatorCSVData";
import _ from "lodash";

const OPERATORS_PER_FETCH = 500;

export const useDirectory = () => {
  const { t } = useTranslation();
  const loggedInAs = useLoggedInAs();
  const { toastFactory } = useToastContext();

  const navigate = useNavigate();
  const { root, appPath } = useCurrentAppRoute();

  const [appViewSections, setAppViewSections] = useState(DirectoryViewSections);
  const [selectedAppItem, setSelectedAppItem] = useState<IAppViewItem>(
    appViewSections[0].items[0],
  );

  const { getFilteredOperators } = useOperatorFilter();
  const [searchValue, setSearchValue] = useState("");

  const handleSearchValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const searchVal = e.target.value;
    setSearchValue(searchVal);
    const operators = getFilteredOperators(
      allOperators.current,
      selectedAppItem.key,
      loggedInAs?.entityId,
      e.target.value,
    );
    setFilteredOperators([...operators]);
    if (selectedAppItem.key === DirectoryKey.All) {
      createAlphabetGroups([...operators]);
    } else {
      setDateSortedOperators(
        _.orderBy([...operators], ["updatedDateISO"], ["desc"]),
      );
    }
  };

  // Update the translations for the app view sections
  useEffect(() => {
    setAppViewSections((prev) =>
      prev.map((section) => ({
        items: section.items.map((item) => ({
          ...item,
          text: t(`directory:HEADER.ACTIONS.${item.key}`),
        })),
      })),
    );
  }, [t]);

  // Main query for fetching operators in batches
  const allOperators = useRef<OperatorWithStatus[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const { error, fetchMore } = useOperatorManyQuery({
    variables: {
      useCase: OperatorManyUseCaseEnum.Internal,
      includeInvitedOrAcceptedStaffCounts: true,
      filter: {
        organizations: loggedInAs?.entityId ? [loggedInAs.entityId] : undefined,
      },
      limit: OPERATORS_PER_FETCH,
      skip: 0,
    },
    onCompleted: (data) => {
      const newOperators = (data.operatorMany as OperatorWithStatus[]) ?? [];
      const hasMore = newOperators.length === OPERATORS_PER_FETCH;
      allOperators.current = [...allOperators.current, ...newOperators];

      // Update filtered operators and groups with all fetched operators
      setHasMore(hasMore);
      setInitialLoad(false);
      createAlphabetGroups([...allOperators.current]);
      setFilteredOperators([...allOperators.current]);

      // Update view counts
      if (!hasMore) {
        setAppViewSections((prev) =>
          prev.map((section) => ({
            items: section.items.map((item) => ({
              ...item,
              count: getFilteredOperators(
                allOperators.current,
                item.key,
                loggedInAs?.entityId,
                searchValue,
              ).length,
              isSelected: selectedAppItem?.key === item.key,
            })),
          })),
        );
      }
    },
    notifyOnNetworkStatusChange: true,
  });

  // Effect to handle fetching batches of operators
  useEffect(() => {
    if (hasMore) {
      fetchMore({
        variables: {
          skip: allOperators.current.length,
        },
        updateQuery: (_, { fetchMoreResult }) => {
          if (!fetchMoreResult?.operatorMany?.length) {
            return { operatorMany: [] };
          }

          // Since we're using no-cache, we need to manually merge the results
          return {
            operatorMany: fetchMoreResult.operatorMany,
          };
        },
      });
    }
  }, [allOperators.current.length, hasMore, fetchMore]);

  const [getOperatorsByIds, { loading: operatorsByIdsLoading }] =
    useOperatorsByIdsLazyQuery();

  const [filteredOperators, setFilteredOperators] = useState<
    OperatorWithStatus[]
  >(allOperators.current);

  const {
    alphabetGroups,
    flattenedAlphabetGroups,
    virtualizedHeight,
    createAlphabetGroups,
  } = useAlphabet();
  const [dateSortedOperators, setDateSortedOperators] = useState<
    OperatorWithStatus[]
  >([]);

  /**
   * When a user selects a view option, get the operators for that view,
   * update the selectedAppItem, and set the selected view
   * @param selectedItem
   */
  const handleAppViewChange = (selectedItem: IAppViewItem) => {
    const operators = getFilteredOperators(
      allOperators.current,
      selectedItem.key,
      loggedInAs?.entityId,
      searchValue,
    );
    setFilteredOperators([...operators]);
    if (selectedItem.key === DirectoryKey.All) {
      createAlphabetGroups([...operators]);
    } else {
      setDateSortedOperators(
        _.orderBy([...operators], ["updatedDateISO"], ["desc"]),
      );
    }

    setSelectedAppItem(selectedItem);
    setAppViewSections((prev) =>
      prev.map((section) => ({
        items: section.items.map((item) => ({
          ...item,
          isSelected: selectedItem.key === item.key,
        })),
      })),
    );
  };

  /**
   * When an operator's arrow is clicked, navigate to the operator details
   * @param operator
   */
  const handleOperatorClick = (operator: OperatorWithStatus) => {
    navigate(`${root}/${appPath}/${operator._entityId}`);
  };

  const handleGetOperatorCsvData = async (): Promise<
    Record<string, string>[]
  > => {
    try {
      const filteredOperatorEntityIds = filteredOperators.map(
        (op) => op._entityId,
      );
      const { data: operatorsData } = await getOperatorsByIds({
        variables: {
          operatorEntityIds: filteredOperatorEntityIds,
        },
      });

      return mapOperatorCSVData(t, operatorsData);
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("core:CSV.ERROR"),
      });
      return [];
    }
  };

  return {
    alphabetGroups,
    flattenedAlphabetGroups,
    virtualizedHeight,
    appViewSections,
    dateSortedOperators,
    organizationId: loggedInAs?.entityId,
    selectedAppItem,
    showAlphabet: filteredOperators.length > 25,
    onAppViewChange: handleAppViewChange,
    onOperatorClick: handleOperatorClick,
    numOperators: filteredOperators.length,
    searchValue,
    onSearchValueChange: handleSearchValueChange,
    loading: initialLoad,
    error,
    hasMore,
    onGetOperatorCsvData: handleGetOperatorCsvData,
    isLoadingOperatorCsvData: operatorsByIdsLoading,
  };
};
